import React, { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authApi } from '@/lib/api/auth'
import { useSupabaseAuth } from '@/lib/hooks'

const DASHBOARD_URL = '/dashboard'

function getSafeRedirect(rawRedirect: string | null): string {
  if (!rawRedirect || !rawRedirect.startsWith('/')) {
    return DASHBOARD_URL
  }

  if (rawRedirect.startsWith('//') || rawRedirect.startsWith('/login')) {
    return DASHBOARD_URL
  }

  return rawRedirect
}

export default function LoginPage(): React.JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { refreshProfile } = useSupabaseAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const redirectUrl = useMemo(() => getSafeRedirect(searchParams.get('redirect')), [searchParams])

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!email || !password) {
      toast.error('メールアドレスとパスワードを入力してください。')
      return
    }

    setIsLoading(true)

    try {
      await authApi.login({ email, password })
      await refreshProfile()
      toast.success('ログインに成功しました。')
      navigate(redirectUrl, { replace: true })
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('予期せぬエラーが発生しました。')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-12 text-foreground">
      <section className="w-full max-w-md space-y-6 rounded-lg border border-border bg-card px-6 py-7 shadow-sm">
        <div className="space-y-2 text-center">
          <p className="text-label-caps">GenbaDock</p>
          <h1 className="text-2xl font-semibold tracking-tight">ログイン</h1>
          <p className="text-sm leading-6 text-muted-foreground">
            アプリを利用するにはメールアドレスとパスワードを入力してください。
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="login-email">メールアドレス</Label>
            <Input
              id="login-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="mail@example.com"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-password">パスワード</Label>
            <Input
              id="login-password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={isLoading}
            />
          </div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'ログイン中...' : 'ログイン'}
          </Button>
        </form>
      </section>
    </main>
  )
}
